<template>
	<footer class="comp-global-footer ibm-font" :class="themeClassName">
		<div class="wrapper">
			<div class="container" v-if="roadmap">
				<!-- 网站线路图 -->
				<div class="road-map">
					<div class="group">
						<div class="head">{{ t('global_footer.company') }}</div>
						<sensor-track
							class="link-wrapper"
							:datas="item.sensorData"
							v-for="(item, index) in COMPANY_CONFIG_LIST"
							:key="index"
						>
							<nuxt-link-enhance
								class="link"
								:to="item.to"
								:project="item.project"
								@click.native="item.handler ? item.handler() : ''"
							>
								{{ item.name }}
							</nuxt-link-enhance>
						</sensor-track>
					</div>
					<div class="group">
						<div class="head">{{ t('global_footer.product') }}</div>
						<sensor-track
							class="link-wrapper"
							:datas="item.sensorData"
							v-for="(item, index) in PRODUCT_CONFIG_LIST"
							:key="index"
						>
							<nuxt-link-enhance
								class="link"
								:to="item.to"
								:project="item.project"
								@click.native="item.handler ? item.handler() : ''"
							>
								{{ item.name }}
							</nuxt-link-enhance>
						</sensor-track>
					</div>
					<div class="group">
						<div class="head">{{ t('global_footer.service') }}</div>
						<sensor-track
							class="link-wrapper"
							:datas="item.sensorData"
							v-for="(item, index) in SERVICE_CONFIG_LIST"
							:key="index"
						>
							<nuxt-link-enhance
								class="link"
								:to="item.to"
								:project="item.project"
								@click.native="item.handler ? item.handler() : ''"
							>
								{{ item.name }}
							</nuxt-link-enhance>
						</sensor-track>
					</div>
					<div class="group">
						<div class="head">{{ t('global_footer.support') }}</div>
						<sensor-track
							class="link-wrapper"
							:datas="item.sensorData"
							v-for="(item, index) in SUPPORT_CONFIG_LIST"
							:key="index"
						>
							<nuxt-link-enhance
								class="link"
								:to="item.to"
								:project="item.project"
								@click.native="item.handler ? item.handler() : ''"
							>
								{{ item.name }}
							</nuxt-link-enhance>
						</sensor-track>
					</div>
				</div>
				<!-- 移动端适用 -->
				<!-- 社交平台 -->
				<!-- 这里比较难维护，先保持原来不变了 -->
				<div class="mobile-road-map">
					<CollapseMenuPicker class="menu-picker" :title="t('global_footer.company')" :list="COMPANY_CONFIG_LIST" />
					<CollapseMenuPicker class="menu-picker" :title="t('global_footer.product')" :list="PRODUCT_CONFIG_LIST" />
					<CollapseMenuPicker class="menu-picker" :title="t('global_footer.service')" :list="SERVICE_CONFIG_LIST" />
					<CollapseMenuPicker class="menu-picker" :title="t('global_footer.support')" :list="SUPPORT_CONFIG_LIST" />
				</div>
				<div class="social">
					<div class="head">{{ t('global_footer.social_media') }}</div>
					<div class="list">
						<!-- LINK_CONFIG_MAP_NEW -->
						<client-only>
							<template v-for="configItem in LINK_CONFIG_MAP_ARRAY">
								<el-popover
									placement="top-start"
									width="200"
									trigger="hover"
									popper-class="footer-popover"
									:visible-arrow="false"
									v-if="configItem?.list.length > 0"
									:key="configItem.configName"
								>
									<div>
										<p v-for="item in configItem.list" :key="item.tagName">
											<a slot="reference" :href="item.tagLink" target="_blank" rel="nofollow noopener noreferrer">{{
												item.tagName
											}}</a>
										</p>
									</div>
									<template #reference>
										<span class="item">
											<a :href="configItem?.mainLink" target="_blank" rel="nofollow noopener noreferrer">
												<span v-if="configItem.icon" :class="`icon ${configItem.iconClassName || 'iconfont2'}`" v-cloak v-html="configItem.icon"></span>
												<img v-if="configItem.svg" width="16" height="16" class="social-icon" :src="configItem.svg" />
											</a>
										</span>
									</template>
								</el-popover>
							</template>
						</client-only>
						<nuxt-link :to="`/community/${locale}`" class="footer-social-more">
							<span></span>
							<span></span>
							<span></span>
						</nuxt-link>
					</div>
				</div>
			</div>
			<!-- copyright -->
			<div class="copyright">2017-{{ new Date().getFullYear() }} © bitmart.com</div>
		</div>
	</footer>
</template>

<script>
import { defineComponent, ref, onMounted, computed } from 'vue';
import { useStore } from '~/common/js/patch';
import { openZendesk, ZENDESK_LANG_MAP, getViewConfig } from '@/frontend-shared/helper/util';
import NuxtLinkEnhance from '../NavigatorBar/components/NuxtLinkEnhance.vue';
import CollapseMenuPicker from './CollapseMenuPicker';
import { SENSOR_EVENT_NAME } from '~/common/js/sensor';
import SensorTrack from '../NavigatorBar/components/SensorTrack.vue';
import twitterIcon from '@/frontend-shared/assets/img/community/twitter.svg?url';
import vkIcon from '@/frontend-shared/assets/img/community/vk.svg?url';
import messages from './i18n';
import { useI18n } from 'vue-i18n'

const PROJECT_MAP = {
	MASTER: 'www', // 主站，二级域名为www，内测环境时二级域名为beta
	DERIVATIVES: 'derivatives', // 衍生品(合约)，二级域名为derivatives，内测环境时二级域名为derivatives-beta
};

export default defineComponent({
	name: 'GlobalFooter',
	components: {
		NuxtLinkEnhance,
		CollapseMenuPicker,
		SensorTrack,
	},
	props: {
		// 显示网站线路图
		roadmap: {
			type: Boolean,
			default: true,
		},
		theme: {
			type: String,
			default: null,
		},
	},
	setup(props) {
		const $store = useStore();
		// const { proxy } = getCurrentInstance();
		// const $t = proxy.$t.bind(proxy);
		const locale = computed(() => {
			return $store.state['locale'];
		});

		const { t } = useI18n({
			messages,
		});

		const termsUrl = computed(() => {
			if (locale.value === 'cn') {
				return '/html/user_zendesk_sso?redirect=/hc/en-us/articles/115004890354-BITMART-USER-AGREEMENT';
			} else {
				return '/html/user_zendesk_sso?redirect=/hc/en-us/articles/115004890354-Terms-of-Use';
			}
		});

		const helpUrl = computed(() => {
			return `/html/user_zendesk_sso?redirect=/hc/${ZENDESK_LANG_MAP.get(locale.value)}`;
		});
		const applyUrl = computed(() => {
			if (locale.value === 'cn') {
				return '/html/user_zendesk_sso?redirect=/hc/zh-cn/articles/360001865554-%E4%B8%8A%E7%BA%BFBitMart-%E5%B8%81%E5%B8%82';
			} else {
				return '/html/user_zendesk_sso?redirect=/hc/en-us/articles/360001865554-Want-to-List-a-Coin-Token-on-BitMart';
			}
		});

		const NewsUrl = computed(() => {
			if (locale.value === 'cn') {
				return '/html/user_zendesk_sso?redirect=/hc/zh-cn/sections/360000817854-%E5%AA%92%E4%BD%93%E6%8A%A5%E9%81%93';
			} else {
				return '/html/user_zendesk_sso?redirect=/hc/en-us/sections/360000817854-Media-';
			}
		});

		const cooperationUrl = computed(() => {
			if (locale.value === 'cn') {
				return '/html/user_zendesk_sso?redirect=/hc/zh-cn/articles/360001865494-%E4%B8%8E%E6%88%91%E4%BB%AC%E5%90%88%E4%BD%9C';
			} else {
				return '/html/user_zendesk_sso?redirect=/hc/en-us/articles/360001865494-Cooperate-with-BitMart';
			}
		});
		const contactUrl = computed(() => {
      return `/html/user_zendesk_sso?redirect=/hc/${ZENDESK_LANG_MAP.get(locale.value)}/requests/new`;
		});
		const careerUrl = computed(() => {
			if (locale.value === 'cn') {
				return '/html/user_zendesk_sso?redirect=/hc/en-us/categories/10942109789723-Career-Opportunities';
			} else {
				return '/html/user_zendesk_sso?redirect=/hc/en-us/categories/10942109789723-Career-Opportunities';
			}
		});

		// 公司列配置
		const COMPANY_CONFIG_LIST = computed(() => {
			return [
				{
					name: t('global_footer.aboutBitMart'),
					to: '/about',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '公司',
						button_name: '关于我们',
					},
				},

				{
					name: t('global_footer.join'),
					handler: () => {
						openZendesk(careerUrl.value, true);
					},
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '公司',
						button_name: '加入我们',
					},
				},

				{
					name: t('global_footer.advisor'),
					to: '/partner',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '公司',
						button_name: '合作伙伴',
					},
				},

				{
					name: t('global_footer.News'),
					handler: () => {
						openZendesk(NewsUrl.value, true);
					},
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '公司',
						button_name: '新闻',
					},
				},

				{
					name: 'Cipholio Ventures',
					to: 'https://www.cipholio.com',

					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '公司',
						button_name: '安珀资本',
					},
				},
			];
		});

		// 产品列配置
		const PRODUCT_CONFIG_LIST = computed(() => {
			return [
				{
					name: t('global_footer.exchange'),
					to: '/trade',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '产品',
						button_name: '交易中心',
					},
				},
				{
					name: t('global_footer.contract'),
					to: {
						path: '/',
						query: { coinType: 'USD' },
					},
					project: PROJECT_MAP.DERIVATIVES,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '产品',
						button_name: '合约交易',
					},
				},
				{
					name: t('global_footer.earn'),
					to: '/earn',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '产品',
						button_name: '理财',
					},
				},

				{
					name: 'Launchpad',
					to: '/launchpad',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '产品',
						button_name: 'Launchpad',
					},
				},

				{
					name: 'NFT',
					to: '/nft',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '产品',
						button_name: 'NFT',
					},
				},

				{
					name: t('global_footer.APIdoc'),
					to: '/open-api-guide',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '产品',
						button_name: 'API',
					},
				},

				{
					name: 'BMX',
					to: '/bmx',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '产品',
						button_name: 'BMX',
					},
				},

				{
					name: t('global_footer.academy'),
					to: '/academy',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '产品',
						button_name: 'Academy',
					},
				},
			];
		});

		// 服务列配置
		const SERVICE_CONFIG_LIST = computed(() => {
			return [
				{
					name: t('global_footer.fees'),
					to: '/fee',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '服务',
						button_name: '费用',
					},
				},

				{
					name: t('global_footer.bug_bounty'),
					to: 'https://hackenproof.com/bitmart-exchange/bitmart-exchange',
					target: '_self',
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '服务',
						button_name: '赏金计划',
					},
				},

				{
					name: t('global_footer.terms'),
					handler: () => {
						openZendesk(termsUrl.value, true);
					},
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '服务',
						button_name: '用户协议',
					},
				},

				{
					name: t('global_footer.privacy_policy'),
					handler: () => {
						openZendesk(
							'/html/user_zendesk_sso?redirect=/hc/en-us/articles/360005848673-BitMart-User-s-Privacy-Policy',
							true
						);
					},
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '服务',
						button_name: '隐私条款',
					},
				},

				{
					name: t('global_footer.affiliate'),
					to: '/affiliate-partner',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '服务',
						button_name: 'Affiliate',
					},
				},
				{
					name: t('global_footer.vip'),
					to: '/vip',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '服务',
						button_name: 'vip',
					},
				},
			];
		});

		// 用户支持列配置
		const SUPPORT_CONFIG_LIST = computed(() => {
			return [
				{
					name: t('global_footer.help'),
					handler: () => {
						openZendesk(helpUrl.value, true);
					},
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '用户支持',
						button_name: '帮助',
					},
				},

				{
					name: t('global_footer.apply'),
					handler: () => {
						openZendesk(applyUrl.value, true);
					},
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '用户支持',
						button_name: '申请上币',
					},
				},

				{
					name: t('global_footer.cooperation'),
					handler: () => {
						openZendesk(cooperationUrl.value, true);
					},
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '用户支持',
						button_name: '市场合作',
					},
				},

				{
					name: t('global_footer.contact'),
					handler: () => {
						openZendesk(contactUrl.value, true);
					},
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '用户支持',
						button_name: '联系我们',
					},
				},
				{
					name: t('global_footer.feedback'),
					to: '/feedback',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '用户支持',
						button_name: '用户反馈',
					},
				},
				{
					name: t('global_footer.channel'),
					to: '/verification',
					project: PROJECT_MAP.MASTER,
					sensorData: {
						sensorEventName: SENSOR_EVENT_NAME.CLICK_BOTTOM_BAR,
						module_name: '用户支持',
						button_name: '官方验证通道',
					},
				},
			];
		});

		// 社交平台配置
		// 静态地址兜底，具体本配置维护在管理站的formaily
		const LINK_CONFIG_MAP = ref({
			twitterConfig: [],
			TGConfig: [],
			facebookConfig: [],
			instagramConfig: [],
			youtubeConfig: [],
			mediumConfig: [],
			linkedinConfig: [],
			redditConfig: [],
			coinmarketcapConfig: [],
			coingeckoConfig: [],
			discordConfig: [],
			tiktokConfig: [],
			vkConfig: [],
		});

		const LINK_CONFIG_MAP_ARRAY = ref([
			{
				configName: 'twitterConfig',
				socialName: 'twitter',
				svg: twitterIcon,
				list: [],
				mainLink: '',
			},
			{
				configName: 'TGConfig',
				socialName: 'telegram',
				icon: '&#xe8db;',
				list: [],
				mainLink: '',
			},
			{
				configName: 'facebookConfig',
				socialName: 'facebook',
				icon: '&#xe66e',
				list: [],
				mainLink: '',
			},
			{
				configName: 'instagramConfig',
				socialName: 'instagram',
				icon: '&#xe81f;',
				list: [],
				mainLink: '',
				iconClassName: 'bm-iconfont-new'
			},
			{
				configName: 'youtubeConfig',
				socialName: 'youtube',
				icon: '&#xe763;',
				list: [],
				mainLink: '',
			},
			{
				configName: 'mediumConfig',
				socialName: 'medium',
				icon: '&#xf1f0;',
				list: [],
				mainLink: '',
			},
			{
				configName: 'linkedinConfig',
				socialName: 'linkedin',
				icon: '&#xe92b;',
				list: [],
				mainLink: '',
			},
			{
				configName: 'redditConfig',
				socialName: 'reddit',
				icon: '&#xe700;',
				list: [],
				mainLink: '',
			},
			{
				configName: 'coinmarketcapConfig',
				socialName: 'coinmarketcap',
				icon: '&#xe603;',
				list: [],
				mainLink: '',
			},
			{
				configName: 'coingeckoConfig',
				socialName: 'coingecko',
				icon: '&#xe60a;',
				list: [],
				mainLink: '',
				iconClassName: 'bm-iconfont'
			},
			{
				configName: 'discordConfig',
				socialName: 'discord',
				icon: '&#xe66e;',
				list: [],
				mainLink: '',
				iconClassName: 'bm-iconfont-new'
			},
			{
				configName: 'tiktokConfig',
				socialName: 'tiktok',
				icon: '&#xec26;',
				list: [],
				mainLink: '',
				iconClassName: 'bm-iconfont-new'
			},
			{
				configName: 'vkConfig',
				socialName: 'vk',
				svg: vkIcon,
				list: [],
				mainLink: '',
			},
		]);

		const LINK_CONFIG_MAP_NEW = ref({
			twitterConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['twitterConfig'],
			},
			TGConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['TGConfig'],
			},
			facebookConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['facebookConfig'],
			},
			instagramConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['instagramConfig'],
			},
			youtubeConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['youtubeConfig'],
			},
			mediumConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['mediumConfig'],
			},
			linkedinConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['linkedinConfig'],
			},
			redditConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['redditConfig'],
			},
			coinmarketcapConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['coinmarketcapConfig'],
			},
			coingeckoConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['coingeckoConfig'],
			},
			discordConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['discordConfig'],
			},
			tiktokConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['tiktokConfig'],
			},
			vkConfig: {
				mainLink: '',
				list: LINK_CONFIG_MAP.value['vkConfig'],
			},
		});

		const themeClassName = computed(() => {
			if (!props.theme) return props.theme;

			if (props.theme === 'light') return 'day-mode';
			if (props.theme === 'dark') return 'night-mode';
		});
		onMounted(() => {
			// 30 * 60 * 1e3
			getViewConfig('footer_config', false).then((resData) => {
				if (resData) {
					for (let key in LINK_CONFIG_MAP_NEW.value) {
						if (!resData[key]) {
							continue;
						}
						const lst = resData[key];
						if (Array.isArray(lst) && lst.length > 0) {
							const list = lst.filter((item) => item.tagName && item.tagLink);
							if (list.length > 0) {
								LINK_CONFIG_MAP_NEW.value[key] = {
									mainLink: list[0].tagLink,
									list: list,
								};
							}
						}
					}
				}

				for (let i = 0; i < LINK_CONFIG_MAP_ARRAY.value.length; i++) {
					const configItem = LINK_CONFIG_MAP_ARRAY.value[i];
					const configName = configItem.configName;
					const configInMap = LINK_CONFIG_MAP_NEW.value[configName];
					if (configInMap && configInMap.list.length > 0) {
						configItem.mainLink = configInMap.list[0].tagLink;
						configItem.list = configInMap.list;
					}
				}
			});
		});

		return {
			COMPANY_CONFIG_LIST,
			PRODUCT_CONFIG_LIST,
			SERVICE_CONFIG_LIST,
			SUPPORT_CONFIG_LIST,
			LINK_CONFIG_MAP,
			locale,
			themeClassName,
			LINK_CONFIG_MAP_ARRAY,
			t,
		};
	},
});
</script>

<style lang="less">
.day-mode {
	--global-footer-color-bd-primary: #e5ebf0;
	--global-footer-color-text-primary: #1a1b1f;
	--global-footer-color-text-primary-300: #3e4648;
	--global-footer-color-bg-primary: #ffffff; // #EEF3F5

	// tooltip定制
	--global-footer-tooltip-color-bg: #fff;
	--global-footer-tooltip-color-text: #3e4648;
	--global-footer-tooltip-color-bg-hover: #f5f9fb;
	--global-footer-tooltip-color-text-hover: #141f21;
}

.night-mode {
	--global-footer-color-bd-primary: #252832;
	--global-footer-color-text-primary: #ffffff;
	--global-footer-color-text-primary-300: #86939b;
	--global-footer-color-bg-primary: #1a1b1f; // #15191e

	// tooltip定制
	--global-footer-tooltip-color-bg: #292c36;
	--global-footer-tooltip-color-text: #86939b;
	--global-footer-tooltip-color-bg-hover: #4a5167;
	--global-footer-tooltip-color-text-hover: #fff;
}
</style>
<style lang="less" scoped>
@import '@/frontend-shared/components/NavigatorBar/assets/css/ui.variables.less';

.comp-global-footer {
	margin-top: 80px;
	box-sizing: border-box;
	background-color: var(--global-footer-color-bg-primary, #fff);
	* {
		box-sizing: border-box;
	}

	.wrapper {
		padding-top: 40px;
		border-top: 1px solid var(--global-footer-color-bd-primary, #e5ebf0);
	}

	.container {
		display: flex;
		max-width: 1200px;
		width: 100%;
		margin: 0 auto;
		flex-wrap: wrap;
		justify-content: center;
	}

	.road-map {
		display: flex;
		flex-wrap: wrap;
	}

	.mobile-road-map {
		display: none;
	}

	.group {
		width: 180px;
		flex: 0 0 auto;
		margin-right: 22px;
	}

	.head {
		color: var(--global-footer-color-text-primary, #1a1b1f);
		font-size: 16px;
		line-height: 20px;
		font-weight: 500;
		margin-bottom: 24px;
	}

	.link-wrapper {
		margin-bottom: 16px;
		&:last-of-type {
			margin-bottom: 0;
		}
	}

	.link {
		color: var(--global-footer-color-text-primary-300, #3e4648);
		font-size: 14px;
		line-height: 18px;
		cursor: pointer;

		&:hover {
			text-decoration: underline;
		}
	}

	.social {
		width: 180px;
		.head {
			color: var(--global-footer-color-text-primary, #1a1b1f);
			font-size: 16px;
			line-height: 20px;
			font-weight: 500;
			margin-bottom: 24px;
		}

		.list {
			display: flex;
			flex-wrap: wrap;
		}

		.item {
			margin-bottom: 18px;
			margin-right: 18px;
			cursor: pointer;

			&:nth-child(5n) {
				margin-right: 0;
			}
		}

		.icon {
			vertical-align: top;
			font-size: 20px;
			color: var(--global-footer-color-text-primary-300, #3e4648);

			&:hover {
				color: var(--global-footer-color-text-primary, #1a1b1f);
			}
		}

		.social-icon {
			.icon();
			vertical-align: middle;
		}
	}

	.copyright {
		padding-top: 80px;
		padding-bottom: 40px;
		color: var(--global-footer-color-text-primary-300, #3e4648);
		font-size: 14px;
		line-height: 22px;
		text-align: center;
	}

	.footer-social-more {
		display: flex;
		justify-content: space-around;
		align-items: center;
		background-color: #3e4648;
		width: 20px;
		height: 20px;
		border-radius: 3px;
		padding: 0 3px;
		// vertical-align: top;
		span {
			width: 3px;
			height: 3px;
			display: inline-block;
			background-color: #fff;
			border-radius: 100px;
			// margin: 0 1px;
		}
	}
}

// 暂用1120代替
@media screen and (max-width: @screen_bmxl) {
	.comp-global-footer {
		width: 100%;
		padding: 0 16px;
	}
}

@media screen and (max-width: @screen_bmlg) {
	.comp-global-footer {
		width: 100%;
		padding: 0 16px;
		.wrapper {
			border-top: none;
		}

		.road-map {
			display: none;
		}
		.mobile-road-map {
			display: initial;
			width: 100%;
			// flex-direction: column;
			.menu-picker {
				border-top: 1px solid var(--global-footer-color-bd-primary, #e5ebf0);
			}
		}

		.social {
			width: 100%;
			border-top: 1px solid var(--global-footer-color-bd-primary, #e5ebf0);
			padding-top: 24px;
			.item {
				margin-bottom: 20px;
				margin-right: 20px;
				&:nth-child(5n) {
					margin-right: 20px;
				}
			}
			.icon {
				font-size: 20px;
			}
		}
		.copyright {
			border-top: 1px solid var(--global-footer-color-bd-primary, #e5ebf0);
			padding-top: 24px;
			padding-bottom: 24px;
		}

		:deep(.el-collapse-item__header) {
			background-color: transparent;
			color: var(--global-footer-color-text-primary, #1a1b1f);
		}
		:deep(.el-collapse-item__wrap) {
			background-color: transparent;
		}

		:deep(.el-collapse-item__content) {
			color: var(--global-footer-color-text-primary-300, #3e4648);
		}
	}
}
</style>

<style lang="less">
.footer-popover {
	padding: 0!important;
	min-width: auto;
	width: 140px;
	border: none;
	box-sizing: border-box;
	padding: 10px 0px;
	background-color: var(--global-footer-tooltip-color-bg, #fff);

	a {
		display: block;
		padding: 0 20px;
		line-height: 40px;
		color: var(--global-footer-tooltip-color-text, #3e4648);
		background-color: var(--global-footer-tooltip-color-bg, #fff);

		&:hover {
			color: var(--global-footer-tooltip-color-text-hover, #141f21);
			background-color: var(--global-footer-tooltip-color-bg-hover, #f5f9fb);
		}
	}
}
</style>
